import React, { useRef, useEffect } from 'react';
import { Mic, MicOff, Camera, CameraOff, Video } from 'lucide-react';

function VideoTile({ stream, label, isMuted, isCameraOff, isLocal, onToggleMute, onToggleCamera }) {
  const videoRef = useRef(null);

  useEffect(() => {
    if (videoRef.current && stream) {
      videoRef.current.srcObject = stream;
    }
  }, [stream]);

  return (
    <div className="video-container relative rounded-2xl overflow-hidden bg-[#0a0512] aspect-video group">
      {stream ? (
        <video ref={videoRef} autoPlay playsInline muted={isLocal}
          className={`w-full h-full object-cover ${isCameraOff ? 'hidden' : ''}`} />
      ) : null}

      {(!stream || isCameraOff) && (
        <div className="absolute inset-0 flex flex-col items-center justify-center bg-[#0a0512]">
          <div className="w-16 h-16 rounded-full bg-gradient-to-br from-[#FF007F]/20 to-[#E0115F]/20 flex items-center justify-center mb-2">
            <Video className="w-8 h-8 text-[#FF007F]/50" />
          </div>
          <p className="text-sm text-slate-400">{label || 'No video'}</p>
        </div>
      )}

      {/* Name overlay */}
      <div className="absolute bottom-0 left-0 right-0 p-3 bg-gradient-to-t from-black/60 to-transparent">
        <div className="flex items-center justify-between">
          <span className="text-sm font-medium truncate">{label || 'User'}</span>
          {isLocal && (
            <div className="flex items-center gap-1">
              <button data-testid="toggle-mute-btn" onClick={onToggleMute}
                className="p-1.5 rounded-full bg-white/10 hover:bg-white/20 transition-all">
                {isMuted ? <MicOff className="w-4 h-4 text-red-400" /> : <Mic className="w-4 h-4" />}
              </button>
              <button data-testid="toggle-camera-btn" onClick={onToggleCamera}
                className="p-1.5 rounded-full bg-white/10 hover:bg-white/20 transition-all">
                {isCameraOff ? <CameraOff className="w-4 h-4 text-red-400" /> : <Camera className="w-4 h-4" />}
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Muted indicator */}
      {isMuted && (
        <div className="absolute top-3 right-3">
          <MicOff className="w-4 h-4 text-red-400" />
        </div>
      )}
    </div>
  );
}

export default function VideoGrid({ localStream, remoteStreams, isMuted, isCameraOff, onToggleMute, onToggleCamera, userName, roomUsers = [] }) {
  const totalStreams = 1 + remoteStreams.size;
  const gridClass = totalStreams <= 1 ? 'grid-cols-1' :
    totalStreams <= 2 ? 'grid-cols-2' :
    totalStreams <= 4 ? 'grid-cols-2' :
    'grid-cols-3';

  const userMap = new Map(roomUsers.map(u => [u.id, u.display_name || u.id]));

  return (
    <div data-testid="video-grid" className={`grid ${gridClass} gap-4 h-full auto-rows-fr`}>
      <VideoTile
        stream={localStream}
        label={userName || 'You'}
        isMuted={isMuted}
        isCameraOff={isCameraOff}
        isLocal={true}
        onToggleMute={onToggleMute}
        onToggleCamera={onToggleCamera}
      />
      {Array.from(remoteStreams.entries()).map(([userId, stream]) => (
        <VideoTile
          key={userId}
          stream={stream}
          label={userMap.get(userId) || userId}
          isMuted={false}
          isCameraOff={false}
          isLocal={false}
        />
      ))}
    </div>
  );
}
